import {RichTextEditor, type RichTextEditorFunctionsType} from '@forecast-it/design-system';
import React, {useCallback, useImperativeHandle, useRef} from 'react';
import {useDraftJsHtmlConversion} from './useDraftJsConversion';
import {type EditorSurface} from '../../../../tracking/amplitude/editorEvents';
import {RichTextEditorProps} from '@forecast-it/design-system/dist/forecast-v2/components/inputs/richTextEditor/RichTextEditor';

type RichTextEditorDraftJsWrapperProps = RichTextEditorProps & {
	peopleMentions?: {id: string; text: string}[];
	initialDraftJsContent: string;
	onDraftJsChanged?: (string) => void;
	'data-cy'?: string;
	id?: string;
	/**
	 * `React.Ref`, not `RefObject`: two consumers pass a callback here
	 * (comment_footer_section, comment_section_comment), which the narrower type had
	 * always been lying about.
	 */
	editorRef?: React.Ref<RichTextEditorFunctionsType>;
	/**
	 * Lets the surface commit the editor's content itself, for the closes that destroy the
	 * editor before its own save can run. See {@link RichTextEditorDraftJsSaveHandle}.
	 */
	saveRef?: React.Ref<RichTextEditorDraftJsSaveHandle>;
	readOnly?: boolean;
	maxHeight?: string;
	placeholder?: string;
	/**
	 * Which editor this is, for analytics. Editors outside the task modal leave it
	 * unset and raise no events — an event that cannot say which surface it came from
	 * is not actionable.
	 */
	surface?: EditorSurface;
};

/**
 * An imperative commit, for surfaces where the blur IS the save.
 *
 * That save is the design system's click-outside export, and it is scheduled from a
 * document `click` listener that unmount removes — so anything that tears the editor down
 * before the click exists discards the edit. The task modal's backdrop dismisses on
 * `mousedown`, which is exactly that gap (PIKA-745), and Escape never produces a click at
 * all. Both close paths call this first, while the editor is still mounted and readable.
 *
 * This is the rich-text equivalent of the `document.activeElement.blur()` those paths
 * already do for the fields that commit on a real DOM blur.
 */
export type RichTextEditorDraftJsSaveHandle = {
	/** Commits what is in the editor right now, exactly as a blur would. */
	saveNow: () => void;
};

export const RichTextEditorDraftJsWrapper = ({
	peopleMentions,
	initialDraftJsContent,
	onDraftJsChanged,
	'data-cy': dataCy,
	id = 'editor',
	editorRef,
	saveRef,
	readOnly,
	maxHeight,
	placeholder,
	surface,
	...rest
}: RichTextEditorDraftJsWrapperProps): React.JSX.Element => {
	// The wrapper needs the editor handle for itself — the conversion hook reads the
	// editor's own serialisation through it, to tell a blur that saved something from a
	// click through the field — so the caller's ref is attached alongside rather than
	// instead.
	const editorHandle = useRef<RichTextEditorFunctionsType | null>(null);
	const attachEditor = useCallback(
		(instance: RichTextEditorFunctionsType | null) => {
			editorHandle.current = instance;
			if (typeof editorRef === 'function') {
				editorRef(instance);
			} else if (editorRef) {
				(editorRef as React.MutableRefObject<RichTextEditorFunctionsType | null>).current = instance;
			}
		},
		[editorRef]
	);
	// Stable identity: the hook captures the baseline in a mount effect keyed on this,
	// and the description rebinds its save callback on every render.
	const getEditorHtml = useCallback(() => editorHandle.current?.getHtmlValue(), []);

	const {initialHtml, convertHtmlToDraftJs, hasUnexportedChange} = useDraftJsHtmlConversion({
		initialDraftJsContent,
		onDraftJsChanged,
		surface,
		getEditorHtml,
	});
	// Called synchronously from the surface's close handler, before React re-renders, so
	// the editor is still live and `getHtmlValue()` returns the real content. Reading it
	// any later does not work: the export serialises by rendering the editor, and React
	// will not run that nested render from inside the commit that unmounts the tree — it
	// hands back an empty string, which would save the field as blank.
	//
	// Read-only editors are skipped rather than left to no-op further down: nothing can
	// have changed, and the consumer's save callback is wired regardless of `readOnly`.
	useImperativeHandle(
		saveRef,
		() => ({
			saveNow: () => {
				if (readOnly) return;
				const html = editorHandle.current?.getHtmlValue();
				if (html === undefined || !hasUnexportedChange(html)) return;
				convertHtmlToDraftJs(html);
			},
		}),
		[readOnly, convertHtmlToDraftJs, hasUnexportedChange]
	);

	return (
		<RichTextEditor
			id={id}
			data-cy={dataCy}
			peopleMentions={peopleMentions}
			onBlur={convertHtmlToDraftJs}
			initialHtml={initialHtml}
			ref={attachEditor}
			readOnly={readOnly}
			maxHeight={maxHeight}
			placeholder={placeholder}
			{...rest}
		/>
	);
};
